"use client";

import dynamic from "next/dynamic";
import type { InvoiceData } from "@/app/schema";

// Empty placeholder to prevent any visual flash during dynamic import
function EmptyPlaceholder() {
  return <div className="h-full w-full bg-white" />;
}

// Clean loading placeholder - just white background, no spinner
function SimpleLoadingPlaceholder() {
  return <div className="h-full w-full bg-white" />;
}

// Dynamic import for HebrewReceiptPdfDocument - reduces initial bundle size by ~144KB
// This component uses @react-pdf/renderer which is a heavy library
export const HebrewReceiptPdfDocument = dynamic<{
  invoiceData: InvoiceData;
  businessType?: "osek-patur" | "eseq-zair";
  documentType?: "original" | "copy";
}>(
  () =>
    import("./hebrew-receipt-pdf-document").then((mod) => ({
      default: mod.HebrewReceiptPdfDocument,
    })),
  {
    ssr: false,
    loading: EmptyPlaceholder,
  },
);

// Dynamic import for PDFPreviewBuffer - prevents SSR issues and reduces bundle
export const PDFPreviewBuffer = dynamic<{
  invoiceData: InvoiceData;
  errorWhileGeneratingPdfIsShown: boolean;
  className?: string;
  documentType?: "original" | "copy";
  businessType?: "osek-patur" | "eseq-zair";
}>(
  () =>
    import("./invoice-pdf-preview/pdf-preview-buffer").then((mod) => ({
      default: mod.PDFPreviewBuffer,
    })),
  {
    ssr: false,
    loading: EmptyPlaceholder,
  },
);

export const InvoicePDFDownloadLink = dynamic(
  () =>
    import("./invoice-pdf-download-link").then(
      (mod) => mod.InvoicePDFDownloadLink,
    ),
  {
    ssr: false,
    loading: () => null,
  },
);

export const ReceiptWizard = dynamic(
  () =>
    import("./receipt-wizard/ReceiptWizard").then((mod) => mod.ReceiptWizard),
  {
    ssr: false,
    loading: SimpleLoadingPlaceholder,
  },
);

// LazyPDFPreview - Intersection Observer wrapper for deferred PDF loading
// Keep dynamic since it's only used on mobile and defers loading
export const LazyPDFPreview = dynamic(
  () =>
    import("./invoice-pdf-preview/lazy-pdf-preview").then(
      (mod) => mod.LazyPDFPreview,
    ),
  {
    ssr: false,
    loading: EmptyPlaceholder,
  },
);

// Re-export types
export type { InvoicePDFDownloadLinkRef } from "./invoice-pdf-download-link";
